import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import {
  BasketEquityChart, BasketMonthlyChart, BasketBotsTable, BasketMonthlyTable, BasketCorrelation,
  BasketProjection, CompoundToggle,
} from "./BasketViews";
import { OosSection } from "./OosSection";
import {
  ArrowLeftIcon, SignOutIcon, TargetIcon, TrophyIcon, RocketLaunchIcon,
  LockKeyIcon, ArrowsClockwiseIcon,
} from "@phosphor-icons/react";

/**
 * v1.11 — Multi-bot portfolio builder.
 * Target: £5000/month across 5 diversified real-tick champion bots.
 */
export default function Portfolio() {
  const { logout } = useAuth();
  const [target, setTarget] = useState(5000);
  const [startBalance, setStartBalance] = useState(5000);
  const [result, setResult] = useState(null);
  const [mode, setMode] = useState("fixed");
  const [loading, setLoading] = useState(false);
  const [locking, setLocking] = useState(false);
  const [lockMsg, setLockMsg] = useState("");

  const build = async () => {
    setLoading(true);
    setLockMsg("");
    try {
      const r = await api.post("/mt5admin/portfolio/build", {
        target_monthly_gbp: Number(target),
        start_balance: Number(startBalance),
      });
      setResult(r.data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Build failed" });
    } finally {
      setLoading(false);
    }
  };

  const loadCurrent = async () => {
    try {
      const r = await api.get("/mt5admin/portfolio/current");
      if (r.data?.has_target) {
        setTarget(r.data.target_monthly_gbp ?? 5000);
        setStartBalance(r.data.start_balance ?? 5000);
        if (r.data.last_result) setResult(r.data.last_result);
      }
    } catch (e) {/* first-run, no target yet */}
  };

  useEffect(() => { loadCurrent(); }, []);

  const lockBasket = async () => {
    if (!result?.basket?.length) return;
    setLocking(true);
    setLockMsg("");
    try {
      const r = await api.post("/mt5admin/portfolio/lock", {
        bot_names: result.basket.map((b) => b.bot_name),
      });
      setLockMsg(`Locked ${r.data.locked.length} bots as flagship portfolio ✓`);
    } catch (e) {
      setLockMsg(e?.response?.data?.detail || "Lock failed");
    } finally {
      setLocking(false);
    }
  };

  const basket = result?.basket || [];
  const view = result?.views?.[mode];
  const shown = view ? { ...result, monthly: view.monthly, stats: view.stats } : result;
  const st = shown?.stats;
  const hasNewFormat = !!st && !!result?.shared_account && !!result?.views;

  return (
    <div data-testid="mt5-portfolio-page" className="relative min-h-screen bg-[#050914] text-[#F1F5F9]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-10 -left-20 w-96 h-96 rounded-full bg-emerald-600/20 blur-3xl" />
        <div className="absolute top-64 right-0 w-[28rem] h-[28rem] rounded-full bg-yellow-400/10 blur-3xl" />
      </div>

      <header className="relative z-10 border-b border-emerald-900/40 backdrop-blur-md bg-[#050914]/80">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/botgenomix-logo-512.png" alt="BotGenomix" className="h-10 w-10 rounded-lg" />
            <div>
              <div className="text-lg font-medium tracking-tight">
                Bot<span className="text-[#FBBF24]">Genomix</span>
                <span className="ml-2 text-[#2ECA7F] mono text-xs uppercase tracking-widest">Portfolio Builder</span>
              </div>
              <div className="mono text-[9px] uppercase tracking-[0.3em] text-[#64748B]">1 best bot per symbol · combined basket account</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin-mt5" data-testid="portfolio-back"
              className="mono text-[10px] uppercase tracking-widest text-[#94A3B8] hover:text-[#F1F5F9] flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-900/40">
              <ArrowLeftIcon size={12} /> MT5 Lab
            </Link>
            <button onClick={logout}
              className="mono text-[10px] uppercase tracking-widest text-[#FF4D4D] hover:text-[#ff8080] flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-900/40">
              <SignOutIcon size={12} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 pt-10 pb-24">
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-[#2ECA7F]">Basket goal</div>
        <h1 className="text-4xl font-medium tracking-tight mt-2">Build a diversified £{target.toLocaleString()}/month basket.</h1>
        <p className="text-[#94A3B8] mt-3 max-w-3xl">
          Picks the most robust bot for each active symbol (proven &gt; robustness battery passed &gt;
          unseen-year passed &gt; unvalidated) and runs them together on ONE shared account: each month every bot
          earns its % return on the shared balance. More symbols = more independent edges towards the target.
        </p>

        <div className="mt-8 border border-[#2ECA7F]/30 rounded-lg p-5 bg-[#0B0D14]/60 flex flex-col md:flex-row gap-4 items-end">
          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-[#64748B]">Target £ / month</label>
            <input data-testid="portfolio-target-input" type="number" value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="mt-1 mono text-sm bg-[#050914] border border-[#1F2937] rounded px-3 py-2 text-[#F1F5F9] focus:outline-none focus:border-[#2ECA7F] w-40" />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-widest text-[#64748B]">Shared account start £</label>
            <input data-testid="portfolio-start-balance-input" type="number" value={startBalance} min={100} step={500}
              onChange={(e) => setStartBalance(e.target.value)}
              className="mt-1 mono text-sm bg-[#050914] border border-[#1F2937] rounded px-3 py-2 text-[#F1F5F9] focus:outline-none focus:border-[#2ECA7F] w-36" />
          </div>
          <button data-testid="portfolio-build-btn" onClick={build} disabled={loading}
            className="mono text-[11px] uppercase tracking-widest px-4 py-2.5 rounded-md hover:scale-[1.02] transition disabled:opacity-40"
            style={{ background: "#2ECA7F", color: "#050914" }}>
            <ArrowsClockwiseIcon size={12} weight="fill" className="inline mr-1" />
            {loading ? "Building…" : "Build basket"}
          </button>
          {result?.basket?.length > 0 && (
            <button data-testid="portfolio-lock-btn" onClick={lockBasket} disabled={locking}
              className="mono text-[11px] uppercase tracking-widest px-4 py-2.5 rounded-md border transition disabled:opacity-40"
              style={{ borderColor: "#FBBF24", color: "#FBBF24", background: "#FBBF2410" }}>
              <LockKeyIcon size={12} weight="fill" className="inline mr-1" />
              {locking ? "Locking…" : "Lock as flagship"}
            </button>
          )}
        </div>
        {lockMsg && <div className="mt-2 mono text-[11px] text-[#FBBF24]">{lockMsg}</div>}

        {result?.error && (
          <div className="mt-6 border border-[#F87171]/40 bg-[#F87171]/5 rounded-lg p-6 mono text-sm text-[#F87171]">
            {result.error}
          </div>
        )}

        {result?.basket && !hasNewFormat && (
          <div data-testid="basket-old-format" className="mt-6 border border-[#FBBF24]/40 rounded-md p-4 mono text-[11px] text-[#FBBF24]">
            This saved basket was built with an older builder. Click Build basket to rebuild it as one shared account.
          </div>
        )}

        {hasNewFormat && (
          <>
            {result.missing_symbols?.length > 0 && (
              <div data-testid="basket-missing-symbols" className="mt-6 border border-[#FBBF24]/40 rounded-md p-3 mono text-[11px] text-[#FBBF24]">
                No profitable bot with monthly data yet for: {result.missing_symbols.join(", ")}
              </div>
            )}
            <CompoundToggle mode={mode} setMode={setMode} />
            <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
              <SummaryTile testid="basket-start-balance" label="Start balance" value={money(st.start_balance)}
                color="#94A3B8" sub={`one shared account · ${basket.length} bots`} />
              <SummaryTile testid="basket-end-balance" label="End balance" value={money(st.end_balance)}
                color="#22D3EE" sub={`+${st.return_pct}% over ${st.months} months`} />
              <SummaryTile testid="basket-net-profit" label="Net profit" value={money(st.net_profit)}
                color={st.net_profit >= 0 ? "#2ECA7F" : "#F87171"} sub={`reports total ${money(st.reported_net_profit)}`} />
              <SummaryTile testid="basket-total-monthly" label="Avg £ / month" value={money(st.avg_monthly)}
                color={result.hits_target ? "#2ECA7F" : "#FBBF24"}
                sub={result.hits_target ? "✓ target met" : `${money(result.shortfall_gbp)} short of ${money(result.target_monthly_gbp)}`} />
              <SummaryTile testid="basket-last12-monthly" label="Avg £ / month · last 12" value={money(st.avg_monthly_last12)}
                color={st.avg_monthly_last12 >= result.target_monthly_gbp ? "#2ECA7F" : "#FBBF24"}
                sub={mode === "compound" ? "compounded" : "fixed lot sizes"} />
              <SummaryTile testid="basket-total-trades" label="Total trades" value={st.total_trades.toLocaleString()}
                color="#F1F5F9" sub={`${st.trades_per_month}/month · win ${st.win_rate}%`} />
              <SummaryTile testid="basket-win-loss-months" label="Win / loss months" value={`${st.profit_months} / ${st.losing_months}`}
                color={st.losing_months <= 4 ? "#2ECA7F" : "#FBBF24"} sub={`of ${st.months} months`} />
              <SummaryTile testid="basket-agg-dd" label="Basket max DD (month-end)" value={`${st.max_dd_pct.toFixed(1)}%`}
                color={st.max_dd_pct < 15 ? "#2ECA7F" : st.max_dd_pct < 25 ? "#FBBF24" : "#F87171"} />
              <SummaryTile testid="basket-best-month" label="Best month" value={money(st.best_month_pnl)}
                color="#2ECA7F" sub={st.best_month} />
              <SummaryTile testid="basket-worst-month" label="Worst month" value={money(st.worst_month_pnl)}
                color={st.worst_month_pnl >= 0 ? "#2ECA7F" : "#F87171"} sub={st.worst_month} />
            </div>

            {result.total_risk_pct > 0 && (
              <div data-testid="basket-risk-note" className={`mt-4 border rounded-md p-3 mono text-[11px] ${result.total_risk_pct > 10 ? "border-[#F87171]/40 text-[#F87171]" : "border-[#1F2937] text-[#94A3B8]"}`}>
                Combined risk if all {basket.length} bots are in a trade at once: {result.total_risk_pct}% of the account
                {result.total_risk_pct > 10 ? " - high for one account; consider lowering RiskPercent on some bots or a bigger balance." : "."}
                {" "}Returns scale with the balance; margin is shared, so a very small balance may skip some trades.
              </div>
            )}

            {!result.all_monthly_complete && (
              <div data-testid="basket-incomplete-warning" className="mt-4 border border-[#FBBF24]/40 rounded-md p-3 mono text-[11px] text-[#FBBF24]">
                Some bots were parsed by the old report reader (pre v1.21), so their monthly rows add up to less than
                their real profit. The combined charts use the monthly rows; bots re-tested on v1.21 show exact figures.
              </div>
            )}

            <BasketEquityChart result={shown} />
            <BasketMonthlyChart result={shown} />
            <BasketProjection projection={result.projection} />
            <BasketBotsTable basket={basket} />
            <BasketMonthlyTable result={shown} />
            <BasketCorrelation result={result} />
            <OosSection mode={mode} />
          </>
        )}

        {!result?.basket && !loading && (
          <div className="mt-8 border border-[#1F2937] rounded-lg p-8 text-center mono text-[12px] text-[#64748B]">
            <TargetIcon size={32} className="mx-auto mb-3 text-[#2ECA7F]/60" />
            Click <span className="text-[#2ECA7F]">Build basket</span> to pick the most robust bot for each active symbol and see the combined basket.
          </div>
        )}
      </div>
    </div>
  );
}

const money = (v) => `£${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function SummaryTile({ testid, label, value, color, sub }) {
  return (
    <div data-testid={testid} className="relative border border-[#1A1C24] bg-[#0B0D14]/60 rounded-lg p-4 overflow-hidden">
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full blur-3xl opacity-40" style={{ background: color }} />
      <div className="relative">
        <div className="mono text-[10px] uppercase tracking-widest text-[#64748B]">{label}</div>
        <div className="mono text-2xl font-medium mt-2" style={{ color }}>{value}</div>
        {sub && <div className="mono text-[9px] text-[#64748B] mt-1">{sub}</div>}
      </div>
    </div>
  );
}
